import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ColDef } from 'ag-grid-community';
import { useReadinessStore } from '../../../store/readinessStore';
import { useEmergencyCategoryStore, useEmergencySubcategoryStore } from '../../../store/masterDataStore';
import { SpinLoader } from '../../../components/loader/Loader';
import { toast } from 'react-toastify';
import Modal from '../../../components/modal/Modal';
import images from '../../../assets/images';
import { Trash2 } from 'lucide-react';

const ReadinessChecklistManagement: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { templates, fetchTemplates, saveTemplate, removeTemplate, loading } = useReadinessStore();
  const { emergencyCategory, fetchAllEmergencyCategory } = useEmergencyCategoryStore();
  const { emergencySubcategory, fetchAllEmergencySubcategory } = useEmergencySubcategoryStore();
  
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: '',
    emergencyCategoryId: '',
    emergencySubcategoryId: '',
    inspectionFrequencyHours: 24,
    checklistConfig: [] as any[],
  });

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      fetchAllEmergencyCategory();
      fetchAllEmergencySubcategory();
    }
  }, [isOpen, fetchTemplates, fetchAllEmergencyCategory, fetchAllEmergencySubcategory]);

  const columnDefs: ColDef[] = useMemo(() => [
    { headerName: 'Title', field: 'title', flex: 1.5 },
    { 
      headerName: 'Category', 
      field: 'emergencyCategoryId',
      valueGetter: (params) => {
        const cat = emergencyCategory.find(s => s._id === params.data.emergencyCategoryId);
        return cat ? cat.categoryName : params.data.emergencyCategoryId;
      },
      flex: 1 
    },
    { 
      headerName: 'Subcategory', 
      field: 'emergencySubcategoryId',
      valueGetter: (params) => {
        const sub = emergencySubcategory.find(s => s._id === params.data.emergencySubcategoryId);
        return sub ? sub.subcategoryName : params.data.emergencySubcategoryId;
      },
      flex: 1 
    },
    { headerName: 'Frequency (Hours)', field: 'inspectionFrequencyHours', width: 150 },
    { headerName: 'Items', valueGetter: (params) => params.data.checklistConfig?.length || 0, width: 100 },
    {
      headerName: 'Actions',
      width: 150,
      cellRenderer: (params: any) => (
        <div className="flex gap-2 p-2">
          <button 
            onClick={() => handleEdit(params.data)}
            className="text-blue-600 hover:text-blue-800"
          >
            <img src={images.editIcon} alt="" width={20} height={20} />
          </button>
          <button 
            onClick={() => handleDelete(params.data.id || params.data._id)}
            className="text-red-600 hover:text-red-800"
          >
            <Trash2 size={20}/>
          </button>
        </div>
      )
    }
  ], [emergencyCategory, emergencySubcategory]);

  const handleEdit = (template: any) => {
    setEditingTemplate(template);
    setFormData({
      title: template.title,
      emergencyCategoryId: template.emergencyCategoryId,
      emergencySubcategoryId: template.emergencySubcategoryId,
      inspectionFrequencyHours: template.inspectionFrequencyHours,
      checklistConfig: template.checklistConfig || [],
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      await removeTemplate(id);
    }
  };

  const handleAddNew = () => {
    setEditingTemplate(null);
    setFormData({
      title: '',
      emergencyCategoryId: '',
      emergencySubcategoryId: '',
      inspectionFrequencyHours: 24,
      checklistConfig: [],
    });
    setIsFormOpen(true);
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      checklistConfig: [
        ...formData.checklistConfig,
        {
          controlId: `item_${Date.now()}`,
          prompt: '',
          type: 'BOOLEAN',
          isCritical: false,
        }
      ]
    });
  };

  const handleRemoveItem = (index: number) => {
    const newConfig = [...formData.checklistConfig];
    newConfig.splice(index, 1);
    setFormData({ ...formData, checklistConfig: newConfig });
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const newConfig = [...formData.checklistConfig];
    newConfig[index] = { ...newConfig[index], [field]: value };
    setFormData({ ...formData, checklistConfig: newConfig });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.emergencyCategoryId || !formData.emergencySubcategoryId) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    const templateData = {
      ...formData,
      id: editingTemplate?.id || editingTemplate?._id,
    };

    const success = await saveTemplate(templateData);
    if (success) {
      setIsFormOpen(false);
    }
  };


  return (
    <Modal isOpen={isOpen} onClose={onClose} modalClass="bg-white rounded-lg p-6 w-full max-w-5xl h-[90vh] flex flex-col relative">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Manage Readiness Checklists</h2>
      </div>

      {!isFormOpen ? (
        <div className="flex-1 flex flex-col">
          <div className="flex justify-end mb-4">
            <button 
              onClick={handleAddNew}
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
              >
                Add New Template
              </button>
            </div>
            <div className="flex-1 ag-theme-alpine w-full">
              <AgGridReact
                rowData={templates}
                columnDefs={columnDefs}
                pagination={true}
                paginationPageSize={10}
              />
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Template Title*</label>
                <input
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded p-2"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Emergency Category*</label>
                <select
                  className="mt-1 block w-full border border-gray-300 rounded p-2"
                  value={formData.emergencyCategoryId}
                  onChange={(e) => setFormData({ ...formData, emergencyCategoryId: e.target.value, emergencySubcategoryId: '' })}
                  required
                >
                  <option value="">Select Category</option>
                  {emergencyCategory.map(cat => (
                    <option key={cat._id} value={cat._id}>{cat.categoryName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Emergency Subcategory*</label>
                <select
                  className="mt-1 block w-full border border-gray-300 rounded p-2"
                  value={formData.emergencySubcategoryId}
                  onChange={(e) => setFormData({ ...formData, emergencySubcategoryId: e.target.value })}
                  required
                  disabled={!formData.emergencyCategoryId}
                >
                  <option value="">Select Subcategory</option>
                  {emergencySubcategory
                    .filter(sub => sub.categoryId === formData.emergencyCategoryId)
                    .map(sub => (
                    <option key={sub._id} value={sub._id}>{sub.subcategoryName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Inspection Frequency (Hours)*</label>
                <input
                  type="number"
                  className="mt-1 block w-full border border-gray-300 rounded p-2"
                  value={formData.inspectionFrequencyHours}
                  onChange={(e) => setFormData({ ...formData, inspectionFrequencyHours: parseInt(e.target.value) })}
                  required
                />
              </div>
            </div>

            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-gray-700">Checklist Items</h3>
                <button 
                  type="button"
                  onClick={handleAddItem}
                  className="text-blue-600 text-sm hover:underline"
                >
                  + Add Item
                </button>
              </div>
              <div className="flex-1 overflow-y-auto mb-4 border rounded p-2 space-y-4 bg-gray-50">
                {formData.checklistConfig.map((item, index) => (
                  <div key={item.controlId} className="bg-white p-4 rounded shadow-sm border border-gray-200">
                    <div className="grid grid-cols-12 gap-3 items-end">
                      <div className="col-span-6">
                        <label className="block text-xs text-gray-500">Prompt / Question</label>
                        <input
                          type="text"
                          className="w-full border-b focus:border-blue-500 transition outline-none py-1"
                          value={item.prompt}
                          onChange={(e) => handleItemChange(index, 'prompt', e.target.value)}
                          placeholder="e.g. Is the pressure gauge in green zone?"
                        />
                      </div>
                      <div className="col-span-3">
                        <label className="block text-xs text-gray-500">Type</label>
                        <select
                          className="w-full border-b bg-transparent outline-none py-1"
                          value={item.type}
                          onChange={(e) => handleItemChange(index, 'type', e.target.value)}
                        >
                          <option value="BOOLEAN">Yes / No</option>
                          <option value="NUMERIC">Numeric Value</option>
                          <option value="PHOTO_PROOF">Photo Proof</option>
                        </select>
                      </div>
                      <div className="col-span-2 flex items-center gap-2 mb-1">
                        <input
                          type="checkbox"
                          checked={item.isCritical}
                          onChange={(e) => handleItemChange(index, 'isCritical', e.target.checked)}
                          id={`critical_${index}`}
                        />
                        <label htmlFor={`critical_${index}`} className="text-xs text-red-600 font-medium">Critical</label>
                      </div>
                      <div className="col-span-1 text-right">
                        <button 
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {formData.checklistConfig.length === 0 && (
                  <p className="text-center text-gray-500 py-10 italic">No items added yet. Click "+ Add Item" to begin.</p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4 border-t pt-4">
              <button 
                type="button" 
                onClick={() => setIsFormOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </form>
        )}
    </Modal>
  );
};

export default ReadinessChecklistManagement;
